import { useCallback, useRef } from 'react';

interface UseInfiniteScrollProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  enabled?: boolean;
  /** Si hay más filas ya en memoria y aún no pintadas (ventana local), se revelan antes que la API */
  hasMoreInMemory?: boolean;
  revealMoreInMemory?: () => void;
}

export const useInfiniteScroll = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  enabled = true,
  hasMoreInMemory = false,
  revealMoreInMemory,
}: UseInfiniteScrollProps) => {
  const observer = useRef<IntersectionObserver | null>(null);

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting || !enabled) return;
          if (hasMoreInMemory && revealMoreInMemory) {
            revealMoreInMemory();
            return;
          }
          if (hasNextPage) {
            fetchNextPage();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '200px',
        },
      );

      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage, enabled, hasMoreInMemory, revealMoreInMemory],
  );

  return { lastElementRef };
};
